/**
 * Marks UI controls that are only meaningful in the interactive app (e.g. the cross section
 * "Show legend" toggle) and must not be part of an exported image or video.
 */
export const EXPORT_IGNORE_ATTRIBUTE = "data-export-ignore";

const HIGHCHARTS_INVISIBLE_TRACKER_CLASS_PATTERN = /(^|\s)highcharts-tracker-(line|area)(?=\s|$)/;
const HIGHCHARTS_A11Y_PROXY_CLASS_PATTERN = /(^|\s)highcharts-a11y-proxy-container(?:-\S+)?(?=\s|$)/;

/**
 * html-to-image clones every node of the captured tree, reading its full computed style. Highcharts
 * SVGs contain a lot of nodes that never contribute a pixel: wind icons hidden by the horizontal
 * thinning (`opacity="0"`), hidden groups and the transparent hover trackers. Skipping them keeps
 * the capture time roughly proportional to what is actually visible.
 */
export function isInvisibleChartNode(node: Element): boolean {
  if (typeof node.getAttribute !== "function") {
    return false;
  }

  if (node.hasAttribute(EXPORT_IGNORE_ATTRIBUTE)) {
    return true;
  }

  if (node.getAttribute("opacity") === "0" || node.getAttribute("visibility") === "hidden") {
    return true;
  }

  const className = node.getAttribute("class") ?? "";
  return (
    HIGHCHARTS_INVISIBLE_TRACKER_CLASS_PATTERN.test(className) || HIGHCHARTS_A11Y_PROXY_CLASS_PATTERN.test(className)
  );
}

/**
 * Combines the export-wide node filter with a caller specific one.
 */
export function createSnapshotNodeFilter(extraFilter?: (node: HTMLElement) => boolean) {
  return (node: HTMLElement): boolean => {
    if (isInvisibleChartNode(node)) {
      return false;
    }
    return extraFilter ? extraFilter(node) : true;
  };
}
